import { Trans } from "@lingui/macro";
import type { ComponentType } from "react";
import { lazy, Suspense } from "react";

/**
 * Route-level lazy import helper.
 *
 * Wrap heavy auth-only routes with `lazyRoute(import("..."), "ExportName")`
 * to defer their bundle until the route actually mounts. This is the
 * mechanism behind perf audit item #6 — without it, recharts (~100 KB),
 * MDX (~80 KB) and Tiptap (~150 KB) ship in the marketing bundle.
 *
 * Marketing pages stay eager so they benefit from prerender / SSG.
 */
export const lazyRoute = <K extends string>(
  importer: () => Promise<Record<K, ComponentType<unknown>>>,
  exportName: K,
): ComponentType<unknown> => {
  const Loaded = lazy(async () => {
    const module = await importer();
    return { default: module[exportName] };
  });
  return Loaded as unknown as ComponentType<unknown>;
};

/**
 * Suspense fallback for route-level lazy boundaries. Intentionally
 * minimal — the user is mid-navigation, not first-load, so we don't need
 * a heavy skeleton. The browser keeps the previous route painted until
 * the new chunk arrives, so this fallback is rarely seen.
 */
const RouteFallback = () => (
  <div
    aria-live="polite"
    aria-busy="true"
    className="flex min-h-[60vh] items-center justify-center text-muted-foreground"
  >
    <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
      <span className="size-1.5 animate-pulse rounded-full bg-primary" />
      <Trans>Loading…</Trans>
    </div>
  </div>
);

/**
 * Wraps `children` in a Suspense boundary tuned for route-level
 * code-splitting. Apply at layout level so every lazy child route shares
 * the same fallback.
 */
export const RouteSuspense = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<RouteFallback />}>{children}</Suspense>
);
